import { useSelector } from "react-redux";

import AppState from "../../store/AppState";
import { selectAllProductsFetched } from "../../store/product/ProductSelectors";
import {
  getHeatMapMetricValueForProduct,
  selectDisplayGradientOnGrid,
  selectHeatMapCacheIsFreshForRange,
  selectHeatMapColorScales,
  selectHeatMapFreshProductCodes,
  selectHeatMapMetric,
  selectProductAnalyticsCache,
} from "../../store/heatmap/HeatMapSelectors";
import { isHeatMapMetricDateReliant } from "./HeatMapMetricSelect";

/**
 * Returns the heat-map gradient color for a single catalog product
 * tile, mirroring the per-cell color the planogram would assign for
 * the same product. Returns `undefined` when:
 *   - the "Display Heat Map Gradient on Grid" flag is off,
 *   - the metric is date-reliant and the product's analytics value
 *     hasn't been settled for the current range,
 *   - the product has no metric value (or no analyticsId for date-
 *     reliant metrics), or
 *   - the color-scale array is empty (no fresh values in the batch).
 *
 * Tiles whose metric value falls outside the scale's distribution are
 * clamped to the nearest band — same behaviour the planogram applies.
 */
export const useHeatMapTileColor = (
  productId: string,
): string | undefined => {
  const enabled = useSelector(selectDisplayGradientOnGrid);
  const metric = useSelector(selectHeatMapMetric);
  const scales = useSelector(selectHeatMapColorScales);
  const product = useSelector(
    (state: AppState) => selectAllProductsFetched(state)[productId],
  );
  const productAnalyticsCache = useSelector(selectProductAnalyticsCache);
  const cacheIsFreshForRange = useSelector(selectHeatMapCacheIsFreshForRange);
  const freshProductCodes = useSelector(selectHeatMapFreshProductCodes);

  if (!enabled) return undefined;
  if (!scales.length) return undefined;
  if (!product) return undefined;

  const isDateReliant = isHeatMapMetricDateReliant(metric);
  const analyticsId = product.analyticsId;

  if (isDateReliant) {
    const heatMapMarkedFresh = Boolean(
      analyticsId && freshProductCodes[analyticsId],
    );
    const cacheMarkedFresh = Boolean(
      cacheIsFreshForRange &&
        analyticsId &&
        productAnalyticsCache[analyticsId],
    );
    if (!heatMapMarkedFresh && !cacheMarkedFresh) return undefined;
  }

  const value = getHeatMapMetricValueForProduct(
    metric,
    product,
    productAnalyticsCache,
  );
  if (value === undefined) return undefined;

  const band = scales.find(
    (s) => value <= s.upperBound && value >= s.lowerBound,
  );
  return band?.color ?? scales[scales.length - 1]?.color;
};
